import React from "react";
import { ArrowUpRight } from "lucide-react";
import WhatsAppIcon from "../icons/WhatsAppIcon";

const STATIONARY_PHOTO =
  "https://customer-assets.emergentagent.com/job_rmaart-store/artifacts/z4hzahox_WhatsApp%20Image%202026-04-27%20at%2022.47.38%20%282%29.jpeg";
const UTENSILS_PHOTO =
  "https://customer-assets.emergentagent.com/job_rmaart-store/artifacts/8eghe3b0_WhatsApp%20Image%202026-04-27%20at%2022.47.38%20%281%29.jpeg";
const JOCKEY_PHOTO =
  "https://customer-assets.emergentagent.com/job_rmaart-store/artifacts/k5hap9i3_WhatsApp%20Image%202026-04-27%20at%2022.47.37%20%282%29.jpeg";
const CLOTHES_PHOTO =
  "https://customer-assets.emergentagent.com/job_rmaart-store/artifacts/xf8eclyr_WhatsApp%20Image%202026-04-27%20at%2022.47.37.jpeg";

const categories = [
  {
    name: "Grocery",
    tag: "Daily Essentials",
    desc: "Fresh produce, pulses, rice, oil, spices and every pantry staple you need — carefully sourced and priced right for your family.",
    image:
      "https://images.pexels.com/photos/5951182/pexels-photo-5951182.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    number: "01",
    testid: "category-grocery",
  },
  {
    name: "Clothes",
    tag: "Fashion for Everyone",
    desc: "Stylish, comfortable clothing for the whole family — from casual everyday wear to premium brands like Jockey. Menswear, womenswear and more.",
    image: CLOTHES_PHOTO,
    number: "02",
    testid: "category-clothes",
  },
  {
    name: "Stationary",
    tag: "For School, College & Office",
    desc: "Notebooks, pens, colour boxes, art supplies, diaries and files — everything students, teachers and professionals need.",
    image:
      "https://images.pexels.com/photos/8715624/pexels-photo-8715624.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    number: "03",
    testid: "category-stationary",
  },
  {
    name: "Bags & Gift Articles",
    tag: "Travel, Carry & Celebrate",
    desc: "School backpacks, travel bags, handbags and thoughtful gift articles — soft toys, photo frames, decorative mugs and premium pieces for every occasion.",
    image: STATIONARY_PHOTO,
    number: "04",
    testid: "category-bags-gifts",
  },
  {
    name: "Utensils & Kitchenware",
    tag: "Your Kitchen, Upgraded",
    desc: "Steel plates, cookware, pressure cookers, tiffins, bottles, storage containers and trusted brands like Nirlon — everything your kitchen needs.",
    image: UTENSILS_PHOTO,
    number: "05",
    testid: "category-utensils",
  },
];

export default function Categories() {
  return (
    <section
      id="categories"
      data-testid="categories-section"
      className="py-20 sm:py-28 bg-[#FBF9F6]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-16">
          <div className="font-latin text-xs uppercase tracking-[0.3em] text-[#9B8B3E] mb-3">
            Our Categories
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1A1813] leading-tight">
            Everything you need, <span className="text-[#9B8B3E] italic">in one place</span>
          </h2>
          <p className="mt-4 text-[#5C5646] text-base sm:text-lg font-latin">
            All your family's daily essentials under one roof — quality, variety and trust.
          </p>
        </div>

        <div className="space-y-14 sm:space-y-20">
          {categories.map((c, i) => (
            <CategoryRow key={c.name} category={c} reverse={i % 2 === 1} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryRow({ category, reverse }) {
  const { name, tag, desc, image, number, testid } = category;
  const waMessage = encodeURIComponent(
    `Hello R Maart! I'd like to inquire about your ${name} section.`
  );
  const waHref = `https://wa.me/917278919919?text=${waMessage}`;

  return (
    <div
      data-testid={testid}
      className={`grid md:grid-cols-12 gap-6 md:gap-10 items-center ${
        reverse ? "md:[&>div:first-child]:order-2" : ""
      }`}
    >
      <div className="md:col-span-7 relative group">
        <div className="relative overflow-hidden rounded-3xl aspect-[16/10] bg-[#F5EFE0] border border-[#E5E1D5]">
          <img
            src={image}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
          <div className="absolute top-5 left-5 bg-white/95 backdrop-blur-sm rounded-full px-4 py-1.5 font-latin text-xs uppercase tracking-[0.2em] text-[#9B8B3E] font-medium">
            {tag}
          </div>
        </div>
      </div>

      <div className="md:col-span-5">
        <div className="flex items-baseline gap-4 mb-4">
          <span className="font-display text-5xl sm:text-6xl text-[#9B8B3E]/25 leading-none font-semibold">
            {number}
          </span>
          <div className="h-[1px] flex-1 bg-[#E5E1D5]" />
        </div>

        <h3 className="font-display text-3xl sm:text-4xl text-[#1A1813] leading-tight mb-4 font-semibold">
          {name}
        </h3>

        <p className="text-[#5C5646] text-base leading-relaxed mb-6 font-latin">
          {desc}
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testid}-whatsapp`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] text-white font-latin text-sm font-medium hover:bg-[#1EB855] transition-all shadow-[0_6px_18px_rgba(37,211,102,0.25)] hover:-translate-y-0.5"
          >
            <WhatsAppIcon className="w-4 h-4" />
            Inquire on WhatsApp
          </a>
          <a
            href="#location"
            className="inline-flex items-center gap-2 font-latin text-[#9B8B3E] hover:text-[#857635] group/link text-sm font-medium px-2"
          >
            <span>Visit our store</span>
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </div>
  );
}
